import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiRequest, ApiError } from "../../api/client";
import type { Conference, ConferenceSession, Language, Speaker } from "../../api/types";
import ConferenceDictionariesPanel from "./ConferenceDictionaries";

function toLocalInput(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const statusColor: Record<string, string> = {
  SCHEDULED: "warn",
  LIVE: "ok",
  PAUSED: "warn",
  ENDED: "bad",
};

export default function SessionsPage() {
  const { conferenceId = "" } = useParams<{ conferenceId: string }>();
  const [conference, setConference] = useState<Conference | null>(null);
  const [sessions, setSessions] = useState<ConferenceSession[] | null>(null);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: "",
    startTime: "",
    endTime: "",
    speakerId: "",
    sourceLanguageCode: "en",
    targetLanguageCodes: [] as string[],
  });

  async function load() {
    const [conf, sess, langs, spk] = await Promise.all([
      apiRequest<Conference>(`/conferences/${conferenceId}`),
      apiRequest<ConferenceSession[]>("/sessions", { query: { conferenceId } }),
      apiRequest<Language[]>("/languages", { auth: false }),
      apiRequest<Speaker[]>("/speakers"),
    ]);
    setConference(conf);
    setSessions(sess);
    setLanguages(langs);
    setSpeakers(spk);
    if (!form.sourceLanguageCode && langs[0]) setForm((f) => ({ ...f, sourceLanguageCode: langs[0].code }));
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conferenceId]);

  function toggleTarget(code: string) {
    setForm((f) => ({
      ...f,
      targetLanguageCodes: f.targetLanguageCodes.includes(code)
        ? f.targetLanguageCodes.filter((c) => c !== code)
        : [...f.targetLanguageCodes, code],
    }));
  }

  async function createSession() {
    setBusy(true);
    setError(null);
    try {
      await apiRequest("/sessions", {
        method: "POST",
        body: {
          conferenceId,
          name: form.name,
          startTime: new Date(form.startTime).toISOString(),
          endTime: new Date(form.endTime).toISOString(),
          speakerId: form.speakerId || null,
          sourceLanguageCode: form.sourceLanguageCode,
          targetLanguageCodes: form.targetLanguageCodes,
        },
      });
      setShowForm(false);
      setForm({ ...form, name: "", startTime: "", endTime: "", speakerId: "", targetLanguageCodes: [] });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to create session");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <Link className="btn-ghost" to="/operator/conferences">
          ← All conferences
        </Link>
      </div>
      <div className="toolbar">
        <h1 style={{ margin: 0, flex: 1 }}>{conference?.name ?? "Sessions"}</h1>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          + New session
        </button>
      </div>

      <ConferenceDictionariesPanel conferenceId={conferenceId} />

      {sessions === null && <p>Loading…</p>}
      {sessions && sessions.length === 0 && <p>No sessions yet for this conference.</p>}
      {sessions && sessions.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Source</th>
              <th>Targets</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.id}>
                <td>{s.name}</td>
                <td style={{ fontWeight: 700 }}>{s.sessionCode}</td>
                <td>{s.sourceLanguageCode.toUpperCase()}</td>
                <td>
                  {s.targetLanguageCodes.map((c) => (
                    <span key={c} className="chip">
                      {c.toUpperCase()}
                    </span>
                  ))}
                </td>
                <td>
                  <span className={`status-pill ${statusColor[s.status] ?? "warn"}`}>
                    <span className="status-dot" />
                    {s.status}
                  </span>
                </td>
                <td>
                  <Link className="btn-ghost" to={`/operator/sessions/${s.id}/control`}>
                    Control →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="modal-backdrop" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>New session</h2>
            <div className="stack">
              <div className="field">
                <label>Session name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
              </div>
              <div className="grid-2">
                <div className="field">
                  <label>Start time</label>
                  <input
                    type="datetime-local"
                    value={form.startTime}
                    onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>End time</label>
                  <input
                    type="datetime-local"
                    value={form.endTime}
                    onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                  />
                </div>
              </div>
              <div className="field">
                <label>Speaker (optional)</label>
                <select value={form.speakerId} onChange={(e) => setForm({ ...form, speakerId: e.target.value })}>
                  <option value="">— none —</option>
                  {speakers.map((sp) => (
                    <option key={sp.id} value={sp.id}>
                      {sp.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Source language (the speaker's language)</label>
                <select
                  value={form.sourceLanguageCode}
                  onChange={(e) => {
                    const nextSource = e.target.value;
                    // A language can't be both the speaker's source and a
                    // listener target at once — if it was already toggled on
                    // as a target before the source was changed to match it,
                    // drop it now instead of silently creating a session
                    // where one language appears twice (source + target).
                    setForm((f) => ({
                      ...f,
                      sourceLanguageCode: nextSource,
                      targetLanguageCodes: f.targetLanguageCodes.filter((c) => c !== nextSource),
                    }));
                  }}
                >
                  {languages.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Target languages (attendees can choose any of these)</label>
                <div className="lang-grid">
                  {languages
                    .filter((l) => l.code !== form.sourceLanguageCode)
                    .map((l) => (
                      <button
                        type="button"
                        key={l.code}
                        className={`lang-choice${form.targetLanguageCodes.includes(l.code) ? " selected" : ""}`}
                        onClick={() => toggleTarget(l.code)}
                      >
                        {l.name}
                      </button>
                    ))}
                </div>
              </div>
              {error && <div className="error-banner">{error}</div>}
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={() => setShowForm(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                disabled={
                  busy || !form.name || !form.startTime || !form.endTime || form.targetLanguageCodes.length === 0
                }
                onClick={createSession}
              >
                {busy ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
